import React from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SettingsIcon from "@mui/icons-material/Settings";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import { ALPHA } from "@shell/styles/designTokens";

import type { InfraConfigUI, TargetHealthUI } from "../../types";

import { deriveBarState } from "./deriveBarState";

export interface InfraStatusBarProps {
  infraConfig: InfraConfigUI | null;
  targets: TargetHealthUI[];
  loading?: boolean;
  onManage: () => void;
  onSetUp: () => void;
}

export function InfraStatusBar({
  infraConfig,
  targets,
  loading = false,
  onManage,
  onSetUp,
}: InfraStatusBarProps): React.ReactElement {
  const theme = useTheme();

  if (loading) {
    return (
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.primary.main, ALPHA.SURFACE_TINT),
          mb: 2,
        }}
      >
        <Skeleton width={240} height={24} />
      </Box>
    );
  }

  const state = deriveBarState(infraConfig, targets);

  const isSuccess = state.variant === "success";
  const icon = isSuccess ? (
    <CheckCircleIcon fontSize="small" color="success" />
  ) : (
    <WarningAmberIcon fontSize="small" color="warning" />
  );

  return (
    <Box
      sx={{
        px: 2,
        py: 1.25,
        borderRadius: 1,
        bgcolor: alpha(
          isSuccess ? theme.palette.success.main : theme.palette.warning.main,
          ALPHA.SURFACE_TINT,
        ),
        mb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Typography variant="body2" fontWeight={500}>
          {state.message}
        </Typography>
      </Stack>

      {state.action === "manage" ? (
        <Button
          size="small"
          startIcon={<SettingsIcon fontSize="small" />}
          onClick={onManage}
          sx={{ textTransform: "none" }}
        >
          Manage
        </Button>
      ) : (
        <Button
          size="small"
          variant="contained"
          onClick={onSetUp}
          sx={{ textTransform: "none" }}
        >
          Set up
        </Button>
      )}
    </Box>
  );
}
